<template>
  <v-row
    class="ticket-show"
  >
    <v-col
      v-if="!Object.keys(ticket).length"
      cols="12"
    >
      <v-skeleton-loader
        v-bind="attrs"
        type="article, actions"
      />
    </v-col>

    <v-col
      v-else
      cols="12"
    >
      <v-card
        class="ticket-show-actions"
        elevation="2"
        :loading="loadingMinSett"
        outlined
        tile
      >
        <v-card-title>
          PDS Payment
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="pdsMinAmountError"
            type="error"
          >
            {{ pdsMinAmountError }}
          </v-alert>
          <template v-else>
            <v-row>
              <v-col v-if="showPendingPaymentNotice">
                <v-card
                  class="pa-5"
                  color="#FB8C00"
                >
                  <div class="pending-payment-note">
                    <b>NOTE: </b> Customer has a pending payment.
                  </div>
                </v-card>
              </v-col>
              <v-col>
                <div
                  class="float-right"
                >
                  <ticket-add-debit-card-dialog
                    :ticket="ticket"
                    @success="handleSuccess($event)"
                  />
                  <ticket-add-checking-dialog
                    :ticket="ticket"
                    @success="handleSuccess($event)"
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="mb-0"
              >
                <v-select
                  v-model="paymentProfile"
                  :items="paymentProfiles"
                  item-value="id"
                  item-title="title"
                  label="Payment Method"
                  outlined
                  required
                  :class="{ 'is-invalid': v$.paymentProfile.$error }"
                  :error-messages="paymentProfileErrors"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="mb-0"
              >
                <v-text-field
                  v-model="paymentDate"
                  label="Payment Date"
                  type="date"
                  :class="{ 'is-invalid': v$.paymentDate.$error }"
                  :error-messages="paymentDateErrors"
                  outlined
                  required
                  @input="v$.paymentDate.$touch()"
                  @blur="v$.paymentDate.$touch()"
                />
                <v-alert
                  v-if="!isPaymentDateValid"
                  prominent
                  dense
                  dark
                  text
                  border="start"
                  color="error"
                  icon="mdi-account-voice"
                >
                  <blockquote class="blockquote">
                    The next available apply date for this
                    <span v-if="isDebitCard">Debit Card</span>
                    <span v-else>ACH account</span>
                    is {{ nextValidApplyDate }}
                  </blockquote>
                </v-alert>
                <v-alert
                  v-else-if="paymentProfile && isSameDayPayment"
                  prominent
                  dense
                  dark
                  text
                  border="start"
                  color="primary"
                  icon="mdi-account-voice"
                >
                  <blockquote class="blockquote">
                    This Payment will be applied immediately
                    <span
                      v-if="!isDebitCard && isSameDayAchEnabled"
                    >
                      but it will not be processed until the next business day,
                      because it is an ACH payment method.
                    </span>
                    <span
                      v-if="isDebitCard"
                    >
                      as long as it processes successfully.
                    </span>
                  </blockquote>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="mb-0"
              >
                <v-select
                  v-model="paymentType"
                  :items="paymentTypes"
                  item-value="id"
                  item-title="title"
                  label="Payment Type"
                  outlined
                  required
                  :class="{ 'is-invalid': v$.paymentType.$error }"
                  :error-messages="paymentTypeErrors"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="mb-0"
              >
                <v-text-field
                  v-model="paymentAmount"
                  prepend-inner-icon="mdi-currency-usd"
                  label="Payment Amount"
                  outlined
                  required
                  type="number"
                  :disabled="paymentType === 13"
                  :class="{ 'is-invalid': v$.paymentAmount.$error }"
                  :error-messages="paymentAmountErrors"
                  @input="$emit('differentAmount', $event)"
                />
              </v-col>
            </v-row>
          </template>
        </v-card-text>
        <v-card-actions v-if="!pdsMinAmountError">
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            :disabled="loadingModel"
            :loading="loadingModel"
            @click="validatePaymentAmount()"
          >
            Make PDS Payment
          </v-btn>

          <v-btn
            color="blue darken-1"
            text
            :disabled="loadingModel"
            :to="{ name: 'ticket-show', params: { id: $route.params.id } }"
          >
            Cancel
          </v-btn>
        </v-card-actions>

        <ConfirmPDSPaymentModal
          v-if="ableToConfirm"
          :ticket="ticket"
          :confirm-modal="confirmModal"
          :payment-profile="paymentProfile"
          :payment-profiles="paymentProfiles"
          :payment-amount="paymentAmount"
          :payment-date="paymentDate"
          :payment-type="paymentType"
          :payment-types="paymentTypes"
          @update-payment="handleUpdatePayment"
          @close-modal="handleCloseConfirmModal"
        />

        <PDSPaymentCreatedModal
          :payment-modal="createdModal"
          :payment-data="paymentData"
          @close-modal="handleCloseCreatedModal"
        />
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { DateTime } from 'luxon-business-days';
import { mapActions, mapState } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { between, required } from '@vuelidate/validators';
import ConfirmPDSPaymentModal from '@/views/Tickets/TicketPDSPayment/ConfirmPDSPaymentModal.vue';
import PDSPaymentCreatedModal from '@/views/Tickets/TicketPDSPayment/PDSPaymentCreatedModal.vue';
import TicketAddDebitCardDialog from './AddPaymentDialogs/TicketAddDebitCardDialog.vue';
import TicketAddCheckingDialog from './AddPaymentDialogs/TicketAddCheckingDialog.vue';

export default {
  name: 'TicketPDSPayment',
  components: {
    PDSPaymentCreatedModal,
    TicketAddCheckingDialog,
    TicketAddDebitCardDialog,
    ConfirmPDSPaymentModal,
  },
  props: {
    ticket: {
      type: Object,
      required: true,
    },
    paymentProfiles: {
      type: Array,
      require: true,
      default() {
        return [];
      },
    },
  },
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      paymentModal: false,

      paymentData: {},

      confirmModal: false,
      createdModal: false,

      loadingMinSett: false,
      loadingModel: false,

      today: DateTime.local().toISODate(),

      paymentAmount: 0,
      paymentProfile: null,
      paymentDate: DateTime.local().toISODate(),
      paymentType: 24,

      minSettlement: 0,
      maxAmount: 0,
      pdsMinAmountError: '',

      paymentTypes: [
        { id: 24, title: 'PDS Payment' },
      ],

      attrs: {
        class: 'mb-6',
        boilerplate: true,
        elevation: 2,
      },
    };
  },
  validations() {
    return {
      paymentAmount: { required, between: between(this.minSettlement, this.maxAmount) },
      paymentType: { required },
      paymentProfile: { required },
      paymentDate: { required },
    };
  },
  computed: {
    ...mapState('settings', ['settings']),
    ...mapState('ticket', ['outboundTicket']),
    ...mapState('loanProLoan', {
      loanAutopays: (state) => state.autopays,
    }),

    ableToConfirm() {
      if (!this.paymentProfile || !this.paymentProfiles.length) {
        return false;
      }

      if (!this.paymentType || !this.paymentTypes.length) {
        return false;
      }

      return !(!this.paymentDate || !this.paymentAmount);
    },

    isSameDayPayment() {
      return this.today === this.paymentDate;
    },
    isSameDayAchEnabled() {
      return this.settings?.brand?.enable_same_day_ach || false;
    },
    isDebitCard() {
      return ((this.paymentProfiles || []).find((x) => x.id === this.paymentProfile)?.type || '') === 'paymentAccount.type.credit';
    },
    nextValidApplyDate() {
      // if payment method is a debit card or same day ACH is enabled, return today
      if (this.isDebitCard || this.isSameDayAchEnabled) {
        return DateTime.local().toISODate();
      }

      const now = DateTime.now().setZone('America/Chicago').toObject();
      const hours = now.hour;
      const daysToIncrement = hours >= 15 ? 2 : 1;

      // else return the next biz day
      return DateTime.local().plusBusiness({ days: daysToIncrement }).toISODate();
    },
    isPaymentDateValid() {
      // prevent from being invalid until a date and profile are selected
      if (!this.paymentDate || !this.paymentProfile) {
        return true;
      }

      return this.paymentDate >= this.nextValidApplyDate;
    },
    paymentAmountErrors() {
      const errors = [];

      if (!this.v$.paymentAmount.$dirty) {
        return errors;
      }
      if (!this.v$.paymentAmount.required) {
        errors.push('Payment Amount is required.');
      }
      if (!this.v$.paymentAmount.between) {
        errors.push(`Payment Amount must be between ${this.v$.paymentAmount.$params.between.min} and ${this.v$.paymentAmount.$params.between.max}.`);
      }

      return errors;
    },
    paymentDateErrors() {
      const errors = [];

      if (!this.v$.paymentDate.$dirty) {
        return errors;
      }
      if (!this.v$.paymentDate.required) {
        errors.push('Payment Date is required.');
      }
      return errors;
    },
    paymentTypeErrors() {
      const errors = [];

      if (!this.v$.paymentType.$dirty) {
        return errors;
      }
      if (!this.v$.paymentType.required) {
        errors.push('Payment Type is required.');
      }
      return errors;
    },
    paymentProfileErrors() {
      const errors = [];

      if (!this.v$.paymentProfile.$dirty) {
        return errors;
      }
      if (!this.v$.paymentProfile.required) {
        errors.push('Payment method is required.');
      }
      return errors;
    },
    showPendingPaymentNotice() {
      const tz = 'America/Chicago';
      const now = DateTime.now().setZone(tz);
      const tomorrow = DateTime.now().setZone(tz).plus({ days: 1 }).startOf('day');

      const tomorrowAutopay = this.loanAutopays.some((payment) => {
        if (!payment.apply_date) {
          return false;
        }
        const date = DateTime.fromISO(payment.apply_date, { zone: tz });
        return date.hasSame(tomorrow, 'day') && now.hour >= 15;
      });

      return Number(this.paymentType) === 13 && tomorrowAutopay;
    },
  },

  watch: {
    ticket() {
      this.getMinSettlement();
    },
    isSameDayPayment(value) {
      this.$emit('immediatePayment', value);
    },
  },

  mounted() {
    if (Object.keys(this.ticket).length) {
      this.getMinSettlement();
    }
  },

  methods: {
    ...mapActions('loanProLoan', ['getAutopays']),
    ...mapActions('notification', ['setNotification']),
    handleUpdatePayment(paymentData) {
      this.paymentData = paymentData;
      this.createdModal = true;
      this.handleCloseConfirmModal();
    },

    handleCloseConfirmModal() {
      this.confirmModal = false;
      this.$emit('resetComponent');
    },

    handleCloseCreatedModal() {
      this.createdModal = false;
    },

    validatePaymentAmount() {
      if (!this.isPaymentDateValid) {
        this.setNotification({
          message: 'Please check the payment date, is invalid',
          color: 'red',
        });
        return;
      }

      if (this.paymentAmount <= 0) {
        this.setNotification({
          message: 'Please check the payment amount, is invalid',
          color: 'red',
        });
        return;
      }

      this.v$.$touch();
      if (!this.v$.$invalid) {
        this.confirmModal = true;
      }
    },

    getMinSettlement() {
      this.loadingMinSett = true;

      return this.$axios
        .get(`api/v1/loanpro-customer/${this.ticket.lpCustomer.id}/rtgs-payment/${this.outboundTicket.customer.id}/min-settlement`)
        .then((response) => {
          this.loadingMinSett = false;

          if (response.status === 200) {
            this.minSettlement = response.data?.data?.minSettlement ?? 0;
            this.maxAmount = response.data?.data?.maxAmount ?? 0;
          } else {
            this.pdsMinAmountError = response.data?.message ?? 'Invalid min settlement amount';
          }
        })
        .catch((err) => {
          if (err.response.status === 404) {
            this.pdsMinAmountError = err.response?.data?.message ?? 'Invalid min settlement amount';
          } else {
            this.pdsMinAmountError = 'Invalid min settlement amount';
          }
          this.loadingMinSett = false;
        });
    },
  },
};
</script>

<style scoped>
.pending-payment-note {
  color: white;
  font-size: 1.3rem;
}
</style>
